// lib/project-list-projection.ts
import { getLayerWorkspaceId } from '#types/types';
import type { StudioWorkspaceVersionDTO, WorkspaceVersion } from '#types/types';
import { getListAssetFileKey } from '#lib/list/listOutputGroups';

type ProjectLayerProjectionRecord = {
  id: string;
  projectId?: unknown;
  workspaceId?: unknown;
  layerWorkspaceId?: unknown;
  imageSrc?: string | null;
  videoSrc?: string | null;
  videoFramesUrls?: readonly string[] | null;
};

type HistoricalVersionProjectionRecord = {
  id: string;
  imageUrl?: string | null;
  status?: unknown;
  isLoopDraft?: unknown;
};

type ProjectWorkspaceProjectionRecord<TVersion extends HistoricalVersionProjectionRecord> = {
  id: string;
  projectId?: unknown;
  versions?: readonly TVersion[] | null;
  runtime?: unknown;
};

type ProjectListProjectionEntry<
  TLayer extends ProjectLayerProjectionRecord,
  TVersion extends HistoricalVersionProjectionRecord,
> =
  | { source: 'project-layer'; layer: TLayer }
  | { source: 'workspace-version'; workspaceId: string; version: TVersion };

const normalizeId = (value: unknown): string =>
  typeof value === 'string' ? value.trim() : '';

const normalizeTimestamp = (value: unknown, fallback: unknown = 0): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const numeric = Number(value);
    if (Number.isFinite(numeric)) return numeric;
    const parsed = Date.parse(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return typeof fallback === 'number' && Number.isFinite(fallback) ? fallback : 0;
};

export const normalizeProjectListTimestamp = normalizeTimestamp;

export const normalizeWorkspaceVersionForList = (
  version: WorkspaceVersion | StudioWorkspaceVersionDTO,
): WorkspaceVersion => {
  const candidate = version as WorkspaceVersion & StudioWorkspaceVersionDTO;
  const status = candidate.status === 'VALIDATED' || candidate.status === 'REJECTED' || candidate.status === 'ARCHIVED'
    ? candidate.status
    : 'CANDIDATE';
  const parentId = typeof candidate.parentId === 'string'
    ? candidate.parentId
    : typeof candidate.parentVersionId === 'string'
      ? candidate.parentVersionId
      : null;
  return {
    id: normalizeId(candidate.id),
    name: normalizeId(candidate.name) || normalizeId(candidate.id),
    timestamp: normalizeTimestamp(candidate.timestamp, candidate.createdAt),
    layerVisibility: Array.isArray(candidate.layerVisibility)
      ? candidate.layerVisibility
        .filter((entry): entry is { id: string; visible: boolean } => Boolean(entry) && typeof entry.id === 'string')
        .map((entry) => ({ id: entry.id, visible: entry.visible !== false }))
      : [],
    imageUrl: typeof candidate.imageUrl === 'string' && candidate.imageUrl.trim().length > 0
      ? candidate.imageUrl.trim()
      : undefined,
    isLoopDraft: candidate.isLoopDraft === true,
    isRenderCacheValid: candidate.isRenderCacheValid === true,
    renderStateHash: typeof candidate.renderStateHash === 'string' ? candidate.renderStateHash : undefined,
    status,
    parentId,
    rejectionReason: candidate.rejectionReason ?? null,
    favorite: candidate.favorite === true,
    annotationComments: Array.isArray(candidate.annotationComments) ? candidate.annotationComments : [],
    lastAnnotationMaskUrl: typeof candidate.lastAnnotationMaskUrl === 'string'
      ? candidate.lastAnnotationMaskUrl
      : undefined,
    annotationMask: null,
    annotationMaskIsDirty: false,
  };
};

const layerResourceUrls = (layer: ProjectLayerProjectionRecord): string[] => [
  layer.imageSrc,
  layer.videoSrc,
  ...(Array.isArray(layer.videoFramesUrls) ? layer.videoFramesUrls : []),
].filter((value): value is string => typeof value === 'string' && value.trim().length > 0);

const isVisibleHistoricalListVersion = (version: HistoricalVersionProjectionRecord): boolean => (
  version.isLoopDraft !== true
);

export const selectProjectLayersForList = <TLayer extends ProjectLayerProjectionRecord>(
  layers: readonly TLayer[],
  projectId: string,
): TLayer[] => {
  const normalizedProjectId = normalizeId(projectId);
  if (!normalizedProjectId) return [];
  return layers.filter((layer) =>
    normalizeId(layer.projectId) === normalizedProjectId && getLayerWorkspaceId(layer) === null,
  );
};

export const buildProjectListProjection = <
  TLayer extends ProjectLayerProjectionRecord,
  TVersion extends HistoricalVersionProjectionRecord,
>(
  projectId: string,
  layers: readonly TLayer[],
  workspaces: readonly ProjectWorkspaceProjectionRecord<TVersion>[],
): ProjectListProjectionEntry<TLayer, TVersion>[] => {
  const normalizedProjectId = normalizeId(projectId);
  if (!normalizedProjectId) return [];

  const projectLayers = selectProjectLayersForList(layers, normalizedProjectId);
  const projectAssetKeys = new Set(projectLayers.flatMap(layerResourceUrls).map(getListAssetFileKey).filter(Boolean));
  const projection: ProjectListProjectionEntry<TLayer, TVersion>[] = projectLayers.map((layer) => ({
    source: 'project-layer',
    layer,
  }));
  const seenVersionIds = new Set<string>();

  workspaces.forEach((workspace) => {
    if (normalizeId(workspace.projectId) !== normalizedProjectId) return;
    const workspaceId = normalizeId(workspace.id);
    if (!workspaceId || !Array.isArray(workspace.versions)) return;
    workspace.versions.forEach((version) => {
      if (!isVisibleHistoricalListVersion(version)) return;
      const versionId = normalizeId(version.id);
      if (!versionId || seenVersionIds.has(versionId)) return;
      seenVersionIds.add(versionId);
      const assetKey = getListAssetFileKey(normalizeId(version.imageUrl));
      if (assetKey && projectAssetKeys.has(assetKey)) return;
      projection.push({ source: 'workspace-version', workspaceId, version });
    });
  });

  return projection;
};
